import React, { useState } from 'react';
import { Button, Input, Select } from '@/components/ui';

interface PlanningPeriod {
  id: string;
  name: string;
  isActive: boolean;
}

interface Scenario {
  id: string;
  name: string;
  description?: string;
  planningPeriodId: string;
  isBaseline: boolean;
}

interface ScenarioFormProps {
  initialData?: Partial<Scenario>;
  onSubmit: (data: Partial<Scenario>) => void;
  onCancel: () => void;
  planningPeriods: PlanningPeriod[];
  selectedPlanningPeriodId?: string;
}

const ScenarioForm: React.FC<ScenarioFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  planningPeriods,
  selectedPlanningPeriodId,
}) => {
  const safePlanningPeriods = planningPeriods || [];
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    planningPeriodId: initialData?.planningPeriodId || selectedPlanningPeriodId || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Ensure planning period selection syncs with provided defaults and available options
  React.useEffect(() => {
    if (!formData.planningPeriodId) {
      const nextId =
        selectedPlanningPeriodId ||
        safePlanningPeriods.find((p) => p.isActive)?.id ||
        safePlanningPeriods[0]?.id ||
        '';
      if (nextId) {
        setFormData((prev) => ({ ...prev, planningPeriodId: nextId }));
      }
    }
  }, [formData.planningPeriodId, selectedPlanningPeriodId, safePlanningPeriods]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Scenario name is required';
    }

    if (!formData.planningPeriodId) {
      newErrors.planningPeriodId = 'Planning period is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const planningPeriodOptions = safePlanningPeriods.map(period => ({
    value: period.id,
    label: `${period.name}${period.isActive ? ' (Active)' : ''}`,
  }));

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 gap-3">
        <Input
          label="Scenario Name"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          error={errors.name}
          placeholder="Enter scenario name"
          required
        />

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-foreground">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Enter scenario description (optional)"
            className="w-full min-h-[72px] px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <Select
          label="Planning Period"
          value={formData.planningPeriodId}
          onChange={(value) => handleChange('planningPeriodId', value)}
          options={planningPeriodOptions}
          error={errors.planningPeriodId}
          required
          disabled={!!selectedPlanningPeriodId}
          placeholder="Select a planning period"
        />
      </div>

      {/* Information Box */}
      <div className="bg-muted rounded-md p-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground mb-1.5">About Scenarios</h4>
        <p className="text-xs text-muted-foreground">
          Scenarios allow you to create different "what-if" versions of your portfolio plan.
          Each scenario can have different allocations and priorities, helping you compare
          different planning approaches before committing to one.
        </p>
        {!initialData && (
          <p className="text-xs text-muted-foreground mt-1.5">
            The new scenario will start empty. You can copy allocations from existing scenarios
            or create new ones from scratch.
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t border-border">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button type="submit">
          {initialData ? 'Update Scenario' : 'Create Scenario'}
        </Button>
      </div>
    </form>
  );
};

export default ScenarioForm;
